import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Building2, Menu, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export default function Navigation() {
  const { t, lang, toggle } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAnchor = (e, hash) => {
    if (location.pathname === '/') {
      e.preventDefault();
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#F9F8F6]/85 backdrop-blur-md border-b border-app' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
          <span className="w-9 h-9 rounded-md bg-primary-app flex items-center justify-center text-white shadow-sm transition-transform group-hover:-rotate-3">
            <Building2 size={18} strokeWidth={2.2} />
          </span>
          <span className="font-serif text-2xl font-semibold text-app tracking-tight">
            PortalFinca
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          <Link
            to="/#producto"
            onClick={(e) => handleAnchor(e, '#producto')}
            className="text-sm text-app-secondary hover:text-app transition-colors"
            data-testid="nav-product"
          >
            {t.nav.product}
          </Link>
          <Link
            to="/#como-funciona"
            onClick={(e) => handleAnchor(e, '#como-funciona')}
            className="text-sm text-app-secondary hover:text-app transition-colors"
            data-testid="nav-how"
          >
            {t.nav.howItWorks}
          </Link>
          <Link
            to="/#precios"
            onClick={(e) => handleAnchor(e, '#precios')}
            className="text-sm text-app-secondary hover:text-app transition-colors"
            data-testid="nav-pricing"
          >
            {t.nav.pricing}
          </Link>
          <Link
            to="/#faq"
            onClick={(e) => handleAnchor(e, '#faq')}
            className="text-sm text-app-secondary hover:text-app transition-colors"
            data-testid="nav-faq"
          >
            {t.nav.faq}
          </Link>
          <Link
            to="/demo"
            className="text-sm text-app-secondary hover:text-app transition-colors"
            data-testid="nav-demo"
          >
            {t.nav.demo}
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            className="hidden sm:flex items-center gap-1 text-xs font-medium tracking-wider text-app-secondary hover:text-app border border-app rounded-full px-3 py-1.5 transition-colors"
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            <span className={lang === 'es' ? 'text-app font-semibold' : ''}>ES</span>
            <span className="text-app-muted">·</span>
            <span className={lang === 'ca' ? 'text-app font-semibold' : ''}>CA</span>
          </button>
          <Link
            to="/#contacto"
            onClick={(e) => handleAnchor(e, '#contacto')}
            className="hidden sm:inline-flex items-center gap-2 bg-primary-app text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-[#8F3E2E] transition-colors"
            data-testid="nav-cta"
          >
            {t.nav.cta}
          </Link>
          <button
            className="lg:hidden p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F9F8F6] border-t border-app" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            <Link to="/#producto" onClick={(e) => handleAnchor(e, '#producto')} className="py-2 text-app-secondary">
              {t.nav.product}
            </Link>
            <Link to="/#como-funciona" onClick={(e) => handleAnchor(e, '#como-funciona')} className="py-2 text-app-secondary">
              {t.nav.howItWorks}
            </Link>
            <Link to="/#precios" onClick={(e) => handleAnchor(e, '#precios')} className="py-2 text-app-secondary">
              {t.nav.pricing}
            </Link>
            <Link to="/#faq" onClick={(e) => handleAnchor(e, '#faq')} className="py-2 text-app-secondary">
              {t.nav.faq}
            </Link>
            <Link to="/demo" className="py-2 text-app-secondary">
              {t.nav.demo}
            </Link>
            <button
              onClick={toggle}
              className="self-start text-xs font-medium tracking-wider text-app-secondary border border-app rounded-full px-3 py-1.5"
            >
              {lang === 'es' ? 'ES · CA' : 'CA · ES'}
            </button>
            <Link
              to="/#contacto"
              onClick={(e) => handleAnchor(e, '#contacto')}
              className="bg-primary-app text-white text-center text-sm font-medium px-4 py-2.5 rounded-md mt-2"
            >
              {t.nav.cta}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
